import React from "react";
import cx from "classnames";
import dayjs from "utils/dayjs";
import { BLUE, GREEN } from "store/constants/date";
import type { Dayjs } from "dayjs";
import { type DateRateFomatted } from "apis/calendarRates";

type DateViewProps = {
  date: Dayjs;
  datePropsFn: (date: Dayjs) => {
    selected?: boolean;
    inBetweenSelected?: boolean;
    color?: "BLUE" | "GREEN";
    disabled?: boolean;
  };
  onHover: (date: Dayjs) => void;
  onClick: (date: Dayjs) => void;
  plainView: boolean;
  dateRate: DateRateFomatted | null;
};

type DateProps = {
  date: Dayjs;
  datePropsFn: (date: Dayjs) => {
    selected?: boolean;
    inBetweenSelected?: boolean;
    color?: "BLUE" | "GREEN";
    disabled?: boolean;
  };
};

type PropsForADateType<T> = T extends Dayjs
  ? {
      disabled: boolean;
      selected: boolean;
      hidden: boolean;
      inBetweenSelected: boolean;
      color: "BLUE" | "GREEN";
    }
  : T extends undefined
  ? {
      hidden: boolean;
      selected: boolean;
      disabled: boolean;
      inBetweenSelected: boolean;
      color: "BLUE" | "GREEN";
    }
  : never;

// Props for a date are:
// - hidden:  Don't render anything
// - disabled: Disable the date (say previous date)
// - selected: Highlight the date
const propsForADate = ({ date, datePropsFn }: DateProps) => {
  if (date) {
    const startOfToday = dayjs().startOf("day");
    const props = datePropsFn(date);

    return {
      disabled: date.isBefore(startOfToday),
      ...props,
    };
  } else {
    return {
      hidden: true,
    };
  }
};

export const DateView = ({
  date,
  datePropsFn,
  onHover,
  onClick,
  plainView,
  dateRate,
}: DateViewProps) => {
  const { hidden, disabled, selected, color, inBetweenSelected } =
    propsForADate({
      date,
      datePropsFn,
    }) as PropsForADateType<typeof date>;

  const active = !hidden && !disabled;

  return (
    <td
      className={cx("font-mulish text-12 leading-15 p-0", {
        "text-gray-disable": disabled,
        "bg-gold-border-disabled text-gold": inBetweenSelected,
        "bg-gold-accent text-white": selected && !inBetweenSelected,
        "cursor-pointer": active,
        "bg-blue-light text-gray-disable": color === BLUE,
        "bg-green-light text-gray-disable": color === GREEN,
      })}
      onMouseEnter={() => active && onHover(date)}
      onClick={() => active && onClick(date)}>
      <div
        className={cx(
          "flex justify-center items-center overflow-hidden relative flex-col gap-2",
          {
            "w-44 h-44": plainView,
            "w-40 h-40": !plainView,
          }
        )}>
        {date && date.date()}
        {date && dateRate && (
          <span
            className={cx("text-8", {
              "text-green-rimba":
                dateRate.rate.isLowest && !selected && !inBetweenSelected,
            })}>
            {dateRate.rate.formatted}
          </span>
        )}
      </div>
    </td>
  );
};
